import React, { useState, useEffect, useCallback } from 'react';
import { Download, X, Smartphone, Wifi } from 'lucide-react';

interface PWAInstallPromptProps {
  onInstall?: () => void;
  className?: string;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface ExtendedNavigator extends Navigator {
  standalone?: boolean;
}

interface WindowWithPWA extends Window {
  install?: () => Promise<void>;
  resetPWAPrompt?: () => void;
}

declare const window: WindowWithPWA;

const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({
  onInstall,
  className = ''
}) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const dismissedRef = React.useRef(false); // Ref para rastrear se foi dispensado

  // Handler para quando o app é instalado
  const handleAppInstalled = useCallback(() => {
    if (import.meta.env.DEV) {
      console.log('[PWA] App instalado com sucesso!');
    }
    setIsInstalled(true);
    setShowPrompt(false);
    setIsInstallable(false);
    setDeferredPrompt(null);
    localStorage.setItem('pwa-install-never-show', 'true');
    onInstall?.();
  }, [onInstall]);

  useEffect(() => {
    // Verificar se já está instalado como PWA
    checkIfInstalled();

    // Verificar se usuário já recusou a instalação
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const neverShow = localStorage.getItem('pwa-install-never-show');

    if (neverShow === 'true') {
      dismissedRef.current = true; // Sincroniza ref com localStorage
      if (import.meta.env.DEV) {
        console.log('[PWA] Usuário escolheu nunca mostrar novamente');
      }
      return;
    }

    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysPassed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);

      // Só mostrar novamente após 30 dias
      if (daysPassed < 30) {
        dismissedRef.current = true; // Sincroniza ref com localStorage
        if (import.meta.env.DEV) {
          console.log('[PWA] Dispensado há', daysPassed.toFixed(1), 'dias - aguardando 30 dias');
        }
        return;
      }
    }

    // Escutar evento beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
      setShowPrompt(true);
      if (import.meta.env.DEV) {
        console.log('[PWA] Install prompt pronto - deferredPrompt capturado!');
        console.log('[PWA] Evento beforeinstallprompt:', e);
      }
    };

    // Verificar se já existe deferredPrompt após 2 segundos
    const checkTimer = setTimeout(() => {
      // CRÍTICO: Verificar se foi dispensado durante o timer
      if (dismissedRef.current) {
        if (import.meta.env.DEV) {
          console.log('[PWA] Timer cancelado - prompt foi dispensado pelo usuário');
        }
        return;
      }

      if (import.meta.env.DEV) {
        console.log('[PWA] Verificando se beforeinstallprompt foi capturado...');
      }

      // Verificar novamente o localStorage antes de mostrar
      const dismissedNow = localStorage.getItem('pwa-install-dismissed');
      const neverShowNow = localStorage.getItem('pwa-install-never-show');

      if (neverShowNow === 'true') {
        if (import.meta.env.DEV) {
          console.log('[PWA] Timer cancelado - usuário escolheu nunca mostrar');
        }
        return;
      }

      if (dismissedNow) {
        const dismissedDate = new Date(dismissedNow);
        const daysPassed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);

        if (daysPassed < 30) {
          if (import.meta.env.DEV) {
            console.log('[PWA] Timer cancelado - dispensado há', daysPassed.toFixed(1), 'dias');
          }
          return;
        }
      }

      // Verificar novamente antes de mostrar o prompt
      if (!dismissedRef.current && !deferredPrompt && !isInstalled) {
        if (import.meta.env.DEV) {
          console.log('[PWA] beforeinstallprompt não capturado, mostrando modo manual');
        }
        setIsInstallable(true);
        setShowPrompt(true);
      }
    }, 2000);

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      clearTimeout(checkTimer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [deferredPrompt, isInstalled, handleAppInstalled]);

  const checkIfInstalled = () => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as ExtendedNavigator).standalone === true ||
      document.referrer.includes('android-app://');

    setIsInstalled(isStandalone);
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;

        if (import.meta.env.DEV) {
          console.log('[PWA] Usuário escolheu:', outcome);
        }

        if (outcome === 'accepted') {
          handleAppInstalled();
        } else {
          setShowPrompt(false);
        }

        setDeferredPrompt(null);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('[PWA] Erro durante instalação:', error);
        }
        // Tentar instalação manual via navigator
        await tryManualInstall();
      }
    } else {
      // Tentar instalação manual via navigator
      await tryManualInstall();
    }
  };

  const tryManualInstall = async () => {
    // Tentar trigger manual da instalação PWA
    try {
      // Verificar se o navegador suporta instalação PWA
      if ('install' in window && window.install) {
        if (import.meta.env.DEV) {
          console.log('[PWA] Tentando instalação manual via API...');
        }
        await window.install();
        handleAppInstalled();
        return;
      }

      // Fallback: Mostrar instruções de instalação manual
      if (import.meta.env.DEV) {
        console.log('[PWA] Mostrando instruções de instalação manual...');
      }
      setShowInstructions(true);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[PWA] Falha na instalação manual:', error);
      }
      setShowInstructions(true);
    }
  };

  const handleDismiss = () => {
    dismissedRef.current = true; // Marca como dispensado
    setShowPrompt(false);
    setIsInstallable(false); // Importante: desativa o estado de instalável também

    // Não mostrar novamente por 30 dias
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());

    if (import.meta.env.DEV) {
      console.log('[PWA] Prompt dispensado - não mostrar por 30 dias');
    }
  };

  const handleNeverShow = () => {
    dismissedRef.current = true; // Marca como dispensado permanentemente
    setShowPrompt(false);
    setIsInstallable(false); // Desativa o estado de instalável
    localStorage.setItem('pwa-install-never-show', 'true');

    if (import.meta.env.DEV) {
      console.log('[PWA] Usuário escolheu nunca mostrar novamente');
    }
  };

  // Detectar navegador e sistema operacional para instruções personalizadas
  const getInstallInstructions = () => {
    return {
      title: "Como Instalar o AppAgendas",
      steps: [
        "1. Clique no menu do navegador (três pontinhos ⋯) ou no símbolo de instalação ⬇",
        "2. Procure por 'Instalar AppAgendas...', 'Adicionar à tela inicial' ou 'Criar atalho'",
        "3. Se disponível, marque 'Abrir como janela dedicada'",
        "4. Confirme a instalação"
      ]
    };
  };


  // Função global para reset (disponível via console para debug)
  useEffect(() => {
    window.resetPWAPrompt = () => {
      localStorage.removeItem('pwa-install-dismissed');
      localStorage.removeItem('pwa-install-never-show');
      setShowPrompt(true);
      setIsInstalled(false);
      setIsInstallable(true);
      if (import.meta.env.DEV) {
        console.log('[PWA] Prompt resetado - deve aparecer agora');
      }
    };

    return () => {
      delete window.resetPWAPrompt;
    };
  }, []);

  if (isInstalled || !showPrompt || !isInstallable) {
    return null;
  }

  // Mostrar instruções de instalação manual
  if (showInstructions) {
    const instructions = getInstallInstructions();

    return (
      <div className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 ${className}`}>
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-amber-100 rounded-lg p-2">
                  <Download className="w-6 h-6 text-amber-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{instructions.title}</h3>
              </div>
              <button
                onClick={() => setShowInstructions(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 mb-6">
              {instructions.steps.map((step, index) => (
                <div key={index} className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-xs font-semibold">
                    {index + 1}
                  </div>
                  <p className="text-sm text-gray-700">{step}</p>
                </div>
              ))}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-xs text-amber-800">
                <strong>Dica:</strong> Após a instalação, você encontrará o AppAgendas no menu de aplicativos do seu sistema e poderá criá um atalho na área de trabalho.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowInstructions(false)}
                className="flex-1 bg-amber-600 text-white font-medium py-2 px-4 rounded-lg hover:bg-amber-700 transition-colors text-sm"
              >
                Entendi
              </button>
              <button
                onClick={() => {
                  setShowInstructions(false);
                  setShowPrompt(false);
                  localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
                }}
                className="px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 ${className}`}>
      <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-lg shadow-2xl border border-amber-500/30 p-4 backdrop-blur-sm">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="flex-shrink-0">
            <div className="bg-white/20 rounded-lg p-2">
              <Smartphone className="w-6 h-6" />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold text-white">Instalar AppAgendas</h3>
              <button
                onClick={handleDismiss}
                className="text-white/70 hover:text-white transition-colors"
                aria-label="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-amber-100 mb-3">
              Instale como um aplicativo no seu computador para acesso rápido e funcionamento offline.
            </p>

            {/* Features list */}
            <div className="space-y-1 mb-3">
              <div className="flex items-center gap-2 text-xs text-amber-100">
                <Wifi className="w-3 h-3" />
                <span>Funciona offline</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-amber-100">
                <Download className="w-3 h-3" />
                <span>Acesso rápido na tela inicial</span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <button
                  onClick={handleInstall}
                  className="flex-1 bg-white text-amber-700 font-medium py-2 px-4 rounded-lg hover:bg-amber-50 transition-colors text-sm"
                >
                  Instalar AppAgendas
                </button>
                <button
                  onClick={handleDismiss}
                  className="px-3 py-2 text-white/80 hover:text-white transition-colors text-sm"
                >
                  Agora não
                </button>
              </div>
              <button
                onClick={handleNeverShow}
                className="text-xs text-white/60 hover:text-white/80 transition-colors underline"
              >
                Não mostrar novamente
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;